import React, { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloseIcon, PlusIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { FileThumbnail } from '../shared/FileThumbnail';
import { getFileType, getFileTypeFromUrl } from '../shared/fileUtils';

export type FileItem = File | { url?: string; id?: string; upload?: string };

export type FileUploaderMultiProps = {
  /** Array of files — new File objects or existing server refs. */
  files: FileItem[];
  /** Called with the updated array after add or remove. */
  onChange: (files: FileItem[]) => void;
  /** Called when an existing (non-File) item is removed. */
  onRemove?: (removed: { url?: string; id?: string; upload?: string }) => void;
  /** MIME types to accept. */
  acceptedFileTypes?: Record<string, string[]>;
  /** Additional className for the grid container. */
  className?: string;
};

const DEFAULT_ACCEPTED = {
  'image/jpeg': [],
  'image/png': [],
  'application/pdf': [],
};

type Preview = {
  src: string;
  isFile: boolean;
  type: 'image' | 'pdf' | 'unknown';
  blobUrl: string | null;
};

export const FileUploaderMulti = ({
  files = [],
  onChange,
  onRemove,
  acceptedFileTypes = DEFAULT_ACCEPTED,
  className,
}: FileUploaderMultiProps) => {
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length > 0) {
        onChange([...files, ...accepted]);
      }
    },
    [files, onChange],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: true,
  });

  const previews: Preview[] = useMemo(
    () =>
      files.map(item => {
        if (item instanceof File) {
          const url = URL.createObjectURL(item);
          return { src: url, isFile: true, type: getFileType(item), blobUrl: url };
        }
        const url = (item as { url?: string }).url ?? '';
        return {
          src: url,
          isFile: false,
          type: getFileTypeFromUrl(url),
          blobUrl: null,
        };
      }),
    [files],
  );

  useEffect(() => {
    return () => {
      previews.forEach(p => {
        if (p.blobUrl) URL.revokeObjectURL(p.blobUrl);
      });
    };
  }, [previews]);

  const handleRemove = (index: number, isFile: boolean) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const removed = files[index];
    const newFiles = files.filter((_, i) => i !== index);
    onChange(newFiles);
    if (!isFile && typeof onRemove === 'function') {
      onRemove(removed as { url?: string; id?: string; upload?: string });
    }
  };

  return (
    <div className={cn('flex flex-wrap gap-3 w-full', className)}>
      {previews.map((preview, idx) => (
        <div key={idx} className="relative w-32 h-32 group">
          <FileThumbnail
            src={preview.src}
            type={preview.type}
            size="full"
            className="!w-full !h-full !rounded-lg"
          />
          <button
            type="button"
            onClick={handleRemove(idx, preview.isFile)}
            className="absolute top-1.5 right-1.5 p-1 bg-white rounded-full shadow-md transition-colors hover:bg-gray-100 z-10"
            aria-label="Remove file">
            <CloseIcon className="size-4 text-gray-600" />
          </button>
        </div>
      ))}

      <div
        {...getRootProps()}
        className="border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors flex flex-col justify-center items-center w-32 h-32 min-w-[8rem] min-h-[8rem]">
        <input {...getInputProps()} />
        <PlusIcon className="size-6 text-gray-400" />
      </div>
    </div>
  );
};
